import type { FilterExpression } from 'types/filters';

/**
 * A kind of thing that may hold a standing — one alternative of `assignee_filter`, which is a
 * flat OR-list of `type`/`label` equality tests rather than a query (`type-data.jsx:268-286`).
 * The caller supplies the universe: the composer resolves it from the org's own type catalog,
 * so no second list of type names is restated inside the design system.
 */
export interface PerspectiveAssigneeKind {
  property: 'type' | 'label';
  value: string;
  label: string;
  /** Why this kind qualifies — rendered as the chip's supplementary title. */
  note?: string;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const sameAssigneeFilter = (expression: FilterExpression, kind: PerspectiveAssigneeKind) =>
  expression.property === kind.property && expression.value === kind.value;

/**
 * "a person, a role or an identity record" — the readable form of the OR-list, said with
 * the caller's own kind labels so the sentence and the chips cannot drift apart
 * (`type-data.jsx:290-295`, `assigneeSay`). An empty list reads as an empty string; the
 * caller decides what "anyone" is called, because the two places that say it phrase it
 * differently. Both joins are translated: a list separator is a language decision, not a
 * punctuation constant.
 */
export function assigneeSentence(
  filter: FilterExpression[],
  assigneeKinds: PerspectiveAssigneeKind[],
  t: Translate
): string {
  const say = (expression: FilterExpression) =>
    (assigneeKinds.find((kind) => sameAssigneeFilter(expression, kind))?.label ?? String(expression.value)).toLowerCase();

  return filter.reduce((sentence, expression, index) => {
    if (index === 0) return say(expression);
    if (index === filter.length - 1) {
      return t('studio.typeEditor.perspectives.editor.orJoin', { list: sentence, last: say(expression) });
    }
    return t('studio.typeEditor.perspectives.editor.commaJoin', { list: sentence, next: say(expression) });
  }, '');
}
